import { useEffect, useState } from "react";
import {
  Boxes,
  Copy,
  Eye,
  EyeOff,
  Plus,
  RefreshCw,
  RotateCw,
  Trash2,
  Power,
} from "lucide-react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";

function copyToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text);
  }
  const el = document.createElement("textarea");
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
  return Promise.resolve();
}

function AppCard({ profile, onUpdate }) {
  const [revealed, setRevealed] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [busy, setBusy] = useState(false);

  const masked = "sk_speculo_" + "•".repeat(24);

  const doCopy = async (value, which) => {
    await copyToClipboard(value);
    if (which === "key") {
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 1500);
    } else {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 1500);
    }
  };

  const doRegenerate = async () => {
    if (!window.confirm("Regenerate API key? The current key will stop working immediately.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/apps/${profile.id}/regenerate-key`);
      onUpdate(data);
      setRevealed(true);
    } finally {
      setBusy(false);
    }
  };

  const doToggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/apps/${profile.id}`, {
        is_active: !profile.is_active,
      });
      onUpdate(data);
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete "${profile.name}"? This purges its telemetry logs.`)) return;
    setBusy(true);
    try {
      await api.delete(`/apps/${profile.id}`);
      onUpdate(null, profile.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="speculo-glass rounded-sm p-6 flex flex-col gap-4"
      data-testid={`app-card-${profile.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h3
              className="text-lg font-bold text-cyan-100 tracking-wide"
              data-testid={`app-card-name-${profile.id}`}
            >
              {profile.name}
            </h3>
            <span
              className={
                "text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-sm border " +
                (profile.is_active
                  ? "text-cyan-300 border-cyan-400/40 bg-cyan-500/10"
                  : "text-slate-500 border-slate-600/40 bg-slate-800/40")
              }
              data-testid={`app-card-status-${profile.id}`}
            >
              {profile.is_active ? "Active" : "Inactive"}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 uppercase tracking-widest mt-1 font-mono">
            Created {new Date(profile.created_at).toLocaleDateString()}
          </p>
        </div>
        <button
          onClick={doDelete}
          disabled={busy}
          className="text-[#ff4d70] hover:bg-[#FF003C]/10 border border-[#FF003C]/30 rounded-sm p-2 transition-all disabled:opacity-40"
          title="Delete app"
          data-testid={`app-card-delete-${profile.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.3em] text-cyan-500/70 mb-1">
          App ID
        </label>
        <div className="flex items-center gap-2">
          <code
            className="flex-1 font-mono text-sm text-cyan-300 bg-[#04121a] border border-cyan-900/60 rounded-sm px-3 py-2"
            data-testid={`app-card-appid-${profile.id}`}
          >
            {profile.app_id}
          </code>
          <button
            onClick={() => doCopy(profile.app_id, "id")}
            className="border border-cyan-500/30 text-cyan-300 rounded-sm p-2 hover:bg-cyan-950/40 transition-all"
            title="Copy App ID"
            data-testid={`app-card-copy-appid-${profile.id}`}
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>
        {copiedId && (
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 mt-1 font-mono">
            Copied to clipboard
          </div>
        )}
      </div>

      <div>
        <label className="block text-[10px] uppercase tracking-[0.3em] text-cyan-500/70 mb-1">
          API Key
        </label>
        <div className="flex items-center gap-2">
          <code
            className="flex-1 font-mono text-sm text-cyan-200 bg-[#04121a] border border-cyan-900/60 rounded-sm px-3 py-2 truncate"
            data-testid={`app-card-apikey-${profile.id}`}
          >
            {revealed ? profile.api_key : masked}
          </code>
          <button
            onClick={() => setRevealed((v) => !v)}
            className="border border-cyan-500/30 text-cyan-300 rounded-sm p-2 hover:bg-cyan-950/40 transition-all"
            title={revealed ? "Hide key" : "Reveal key"}
            data-testid={`app-card-reveal-${profile.id}`}
          >
            {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          <button
            onClick={() => doCopy(profile.api_key, "key")}
            className="border border-cyan-500/30 text-cyan-300 rounded-sm p-2 hover:bg-cyan-950/40 transition-all"
            title="Copy API key"
            data-testid={`app-card-copy-apikey-${profile.id}`}
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>
        {copiedKey && (
          <div className="text-[10px] uppercase tracking-widest text-cyan-400 mt-1 font-mono">
            Copied to clipboard
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-cyan-500/10">
        <button
          onClick={doToggle}
          disabled={busy}
          className={
            "flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-widest border transition-all disabled:opacity-40 " +
            (profile.is_active
              ? "border-slate-500/40 text-slate-300 hover:bg-slate-800/50"
              : "border-cyan-400/40 text-cyan-300 hover:bg-cyan-950/40")
          }
          data-testid={`app-card-toggle-${profile.id}`}
        >
          <Power className="w-3.5 h-3.5" />
          {profile.is_active ? "Deactivate" : "Activate"}
        </button>
        <button
          onClick={doRegenerate}
          disabled={busy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-widest border border-cyan-500/40 text-cyan-300 hover:bg-cyan-950/40 transition-all disabled:opacity-40"
          data-testid={`app-card-regenerate-${profile.id}`}
        >
          <RotateCw className="w-3.5 h-3.5" />
          Regenerate Key
        </button>
      </div>
    </div>
  );
}

function CreateAppForm({ onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/apps", { name: name.trim() });
      onCreated(data);
      setName("");
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to create app");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="speculo-glass rounded-sm p-5 flex flex-col gap-3" data-testid="create-app-form">
      <div className="flex items-center gap-2 text-cyan-300">
        <Plus className="w-4 h-4" />
        <h3 className="text-sm uppercase tracking-widest font-semibold">Register New App</h3>
      </div>
      <input
        type="text"
        placeholder="e.g. Nova Analytics"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="bg-[#04121a] border border-cyan-900/60 focus:border-cyan-400/70 rounded-sm px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-500 transition-all"
        data-testid="create-app-name-input"
        required
      />
      {error && (
        <div
          className="text-xs text-[#ff4d70] border border-[#FF003C]/40 bg-[#FF003C]/10 rounded-sm px-3 py-2 font-mono"
          data-testid="create-app-error"
        >
          {typeof error === "string" ? error : "Failed to create app"}
        </div>
      )}
      <button
        type="submit"
        disabled={busy}
        className="self-start flex items-center gap-2 px-4 py-2 bg-cyan-400 text-[#040d14] font-bold uppercase tracking-widest text-xs rounded-sm hover:bg-cyan-300 hover:shadow-[0_0_18px_rgba(0,229,255,0.5)] transition-all disabled:opacity-50"
        data-testid="create-app-submit"
      >
        <Plus className="w-3.5 h-3.5" />
        {busy ? "Provisioning…" : "Provision"}
      </button>
    </form>
  );
}

export default function Apps() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const { data } = await api.get("/apps");
      setApps(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to load apps");
    }
  };

  useEffect(() => {
    (async () => {
      await load();
      setLoading(false);
    })();
  }, []);

  const onRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const onCardUpdate = (updated, deletedId) => {
    if (deletedId) {
      setApps((prev) => prev.filter((a) => a.id !== deletedId));
      return;
    }
    setApps((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
  };

  const onCreated = (created) => {
    setApps((prev) => [created, ...prev]);
  };

  return (
    <Layout>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-cyan-100 tracking-[0.12em] uppercase flex items-center gap-3">
            <Boxes className="w-6 h-6 text-cyan-300" /> Applications
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-lg">
            Every app carries a public <span className="text-cyan-300 font-mono">app_id</span> and a
            secret <span className="text-cyan-300 font-mono">api_key</span>. Keep keys off the
            client side.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 rounded-sm border border-cyan-500/30 text-cyan-300 uppercase tracking-widest text-xs hover:bg-cyan-950/40 transition-all disabled:opacity-50"
          data-testid="apps-refresh-button"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Syncing" : "Refresh"}
        </button>
      </div>

      <div className="mb-6">
        <CreateAppForm onCreated={onCreated} />
      </div>

      {error && (
        <div
          className="mb-6 text-sm text-[#ff4d70] border border-[#FF003C]/40 bg-[#FF003C]/10 rounded-sm px-4 py-3 font-mono"
          data-testid="apps-error"
        >
          {typeof error === "string" ? error : "Failed to load"}
        </div>
      )}

      <section
        className="grid grid-cols-1 lg:grid-cols-2 gap-4"
        data-testid="apps-grid"
      >
        {loading &&
          [1, 2].map((n) => (
            <div key={n} className="speculo-glass rounded-sm p-6 h-52 animate-pulse">
              <div className="h-4 w-32 bg-cyan-500/10 rounded-sm mb-4" />
              <div className="h-3 w-full bg-cyan-500/5 rounded-sm mb-3" />
              <div className="h-3 w-3/4 bg-cyan-500/5 rounded-sm mb-3" />
              <div className="h-3 w-1/2 bg-cyan-500/5 rounded-sm" />
            </div>
          ))}
        {!loading &&
          apps.map((profile) => (
            <AppCard key={profile.id} profile={profile} onUpdate={onCardUpdate} />
          ))}
        {!loading && apps.length === 0 && (
          <div
            className="col-span-full speculo-glass rounded-sm p-10 text-center text-slate-400"
            data-testid="apps-empty"
          >
            No applications yet — provision one above to start collecting telemetry.
          </div>
        )}
      </section>
    </Layout>
  );
}
